<template>
  <teleport :to="teleportTo">
    <transition
      name="fade"
    >
      <div class="iframe-container" 
        :class="{dragging:dragging,unexpand:!expand}" 
        :style="iframeWrapStyleText" 
        @click="test"
        v-show="show"
      >
        <template v-if="draggable">
          <div class="drag-area"
            :class="{unexpand:!expand}"
            @mousedown="onMounseDown" 
            @mouseup="onMouseUp"
          >
            <div class="drag-area-inner">{{$t('common.clickToMove')}}</div>
          </div>
        </template>

        <template v-if="extensible">
          <i v-if="expand" class="expand-icon" @click="expand=false">-</i>
          <i v-else class="unexpand-icon" @click="expand=true">+</i>
        </template>

        <i v-if="closable" class="close-icon" @click="emit('update:show', false)">×</i>

        <i v-if="refreshable" class="refresh-icon" @click="refresh"></i>
       
        <div v-if="loading" class="loading">loading ...</div>

        <iframe 
          :src="url"
          :width="width"
          :height="height"
          frameborder="0"
          ref="iframeElem"
        ></iframe>
      </div>
    </transition>
    <div class="iframe-mask" v-if="dragging"></div>
  </teleport>
</template>

<script lang="ts" setup>

import { computed, onMounted, onUnmounted, ref, toRefs, defineEmits, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
const {t: $t} = useI18n();

const props = withDefaults(defineProps<{
  url: string; // 页面地址
  width: number; // 宽
  height: number; // 高
  show: boolean; // 容器是否展示
  draggable?: boolean; // 是否可拖拽
  extensible?: boolean; // 是否可收缩
  closable?:boolean;  // 是否显示关闭按钮
  refreshable?:boolean;  // 是否显示刷新按钮
  posX?: number; // 出现的位置 left 值
  posY?: number; // 出现的位置 top 值
  teleportTo?: string; // Teleport to
}>(), 
{ 
  width: 375, 
  height: 667 ,
  closable: true,
  refreshable: true,
  teleportTo: 'body',
  draggable: false,
}
);

const emit = defineEmits(['update:show']);

const { url, width, height, draggable, extensible, posX, posY, closable, refreshable, teleportTo} = toRefs(props);
const iframeElem = ref<HTMLElement>();
const expand = ref(true);
const dragging = ref(false);
const loading = ref(true);
const iframeWrapStyle = reactive({
  translateX: 0,
  translateY: 0
});
const iframeWrapStyleText = computed(() => {
  let csrStyle = {};
  if(posX?.value&&posY?.value){
    csrStyle = {
      left: `${posX?.value}px`,
      top: `${posY?.value}px`,
    };
  } else if(!import.meta.env.SSR){
    csrStyle = {
      left: `${(window.innerWidth-width.value)/2}px`,
      top: `${(window.innerHeight-height.value)/2}px`,
    };
    
  }
  return {
    transform: `translate(${iframeWrapStyle.translateX}px, ${iframeWrapStyle.translateY}px)`,
    width: `${width.value}px`,
    height: `${height.value}px`,
    ...csrStyle
  };
});
const dragStartMousePos = {
  x:0,
  y:0
};
const dragStartIframePos = {
  x:0,
  y:0
};
let mouseEndTimer:number;


const onMounseDown = (e: MouseEvent) => {
  if(!draggable.value){
    return;
  }
  dragging.value = true;
  dragStartMousePos.x = e.clientX;
  dragStartMousePos.y = e.clientY;
  dragStartIframePos.x = iframeWrapStyle.translateX;
  dragStartIframePos.y = iframeWrapStyle.translateY;
};

const onMouseMove = (e: MouseEvent) => {
  // console.log('onMouseMove');
  if(!draggable.value){
    return;
  }
  if(!dragging.value){
    return;
  }
  // console.log(e.clientX, e.clientY );
  iframeWrapStyle.translateX = dragStartIframePos.x + (e.clientX - dragStartMousePos.x);
  iframeWrapStyle.translateY = dragStartIframePos.y + (e.clientY - dragStartMousePos.y);

  if(mouseEndTimer){
    clearTimeout(mouseEndTimer);
  }
  mouseEndTimer = setTimeout(onMouseUp, 1000);
};

const onMouseUp = (e: MouseEvent) => {
  if(!draggable.value){
    return;
  }
  dragging.value = false;
};

const test = () => {
  // console.log('test test');
};

const refresh = () => {
  console.log(iframeElem?.value?.contentWindow?.location.reload());
};

onMounted(() => {
  document.body.addEventListener('mousemove', onMouseMove);
  iframeElem.value.onload = () => {
    loading.value = false;
    console.log(`[IframeView] onload: url=${url.value}`);
  };
  // document.domain = 'bilibili.tv';
});

onUnmounted(() => {
  document.body.removeEventListener('mousemove', onMouseMove);
});


</script>

<style lang="less" scoped>
@import "@/styles/var.less";

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.iframe-container {
  background-color: rgba(244, 244, 244, 0.5);
  overflow: hidden;
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  border-radius: 6px;
  border: 1px solid rgba(@color-text-secondary, 20%);
  box-shadow: 0 4px 10px 0 rgba(@color-text-secondary, 20%);
  transition: height 0.3s;
  transform: translate3d(0, 0, 0);

  &.unexpand {
    height: 40px !important;
  }

  &.dragging {
    opacity: 0.9;

    .drag-area {
      height: 100%;
    }
  }

  .drag-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 42px;
    cursor: grab;
    font-size: 0;

    .drag-area-inner {
      width: 100%;
      height: 42px;
      background-color: rgba(0, 0, 0, 0.2);
      text-align: center;
      color: #eee;
      line-height: 42px;

      &:hover {
        font-size: 12px;
        background-color: rgba(0, 0, 0, 0.7);
      }
    }
  }

  .close-icon {
    position: absolute;
    z-index: 1;
    right: 2px;
    top: 2px;
    width: 32px;
    height: 32px;
    font-size: 32px;
    line-height: 1;
    text-align: center;
    color: #dedede;
    cursor: pointer;
    font-weight: 300;
  }

  .refresh-icon {
    width: 18px;
    height: 18px;
    line-height: 1;
    position: absolute;
    z-index: 1;
    left: 14px;
    top: 12px;
    cursor: pointer;
    background: url('@/assets/iframe-view/refresh.png') no-repeat center center;
    background-size: 100%;
  }

  .loading {
    position: absolute;
    top: 220px;
    width: 100%;
    line-height: 1.5;
    font-size: 14px;
    color: #777;
    text-align: center;
  }

  .expand-icon,
  .unexpand-icon {
    position: absolute;
    left: 10px;
    top: 10px;
    width: 20px;
    height: 20px;
    text-align: center;
    line-height: 16px;
    border: 1px solid #666;
    color: #666;
    border-radius: 50%;
    cursor: pointer;
    background-color: #eee;
  }
}

.iframe-mask {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 9999;
}
</style>
